import { useEffect, useRef, useState, type ReactNode } from 'react';
import type * as LeafletNS from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Icon } from '../components/Icon';
import { StatusPill } from '../components/StatusPill';
import { ConfirmActionModal } from '../components/ConfirmActionModal';
import { CaseNotifications } from '../components/CaseNotifications';
import {
  FLAG_TYPES, payerColor, parseDateOnly, ymdLocal, runPrefixFrom, formatCents, formatRelative,
  type Case, type FlagKind,
} from '../data/sampleData';
import { api } from '../lib/api';
import { CaseFbcUpdates } from '../components/FbcUpdates';

interface UserRow { id: number; email: string; name: string | null; role: string; status: string; }

function AssignButton({ caseId, currentAssignee }: { caseId: string; currentAssignee: { id: number; name: string | null; email: string | null } | null }) {
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState<UserRow[] | null>(null);
  const [busy, setBusy] = useState(false);
  const numericId = caseId.replace(/^CASE-/, '');

  useEffect(() => {
    if (!open || users) return;
    api<{ users: UserRow[] }>('/api/admin/users')
      .then((d) => setUsers(d.users.filter((u) => u.status === 'active')
        // Decision 0007: FBCs are assignable too, listed after admins and reviewers.
        .sort((a, b) => Number(a.role === 'fbc') - Number(b.role === 'fbc') || (a.name ?? a.email).localeCompare(b.name ?? b.email))))
      .catch(() => setUsers([]));
  }, [open, users]);

  const assign = async (userId: number | null) => {
    setBusy(true);
    try {
      await api(`/api/cases/${numericId}/assign`, { method: 'PUT', body: { user_id: userId } });
      setOpen(false);
      // hard reload so the list/detail refresh assignee everywhere
      window.location.reload();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Assign failed');
    } finally { setBusy(false); }
  };

  return (
    <div style={{ position: 'relative' }}>
      <button className="btn" onClick={() => setOpen((v) => !v)} disabled={busy}>
        <Icon name="user" size={13} />
        {currentAssignee
          ? ` ${currentAssignee.name ?? currentAssignee.email ?? `User ${currentAssignee.id}`}`
          : ' Assign'}
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', right: 0, marginTop: 4,
          background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6,
          minWidth: 240, padding: 4, zIndex: 1000, boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
        }}>
          {!users && <div className="muted" style={{ padding: 10, fontSize: 12 }}>Loading…</div>}
          {users && currentAssignee && (
            <div onClick={() => assign(null)} style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 12.5, color: 'var(--text-muted)', borderBottom: '1px solid var(--border)' }}>
              Unassign
            </div>
          )}
          {users && users.length === 0 && <div className="muted" style={{ padding: 10, fontSize: 12 }}>No active users.</div>}
          {users?.map((u) => (
            <div key={u.id} onClick={() => assign(u.id)} style={{
              padding: '8px 12px', cursor: 'pointer', fontSize: 12.5,
              background: currentAssignee?.id === u.id ? 'var(--accent-soft)' : undefined,
            }}>
              <div>{u.name ?? u.email}</div>
              <div className="faint" style={{ fontSize: 11 }}>{u.email} · {u.role === 'fbc' ? 'FBC' : u.role}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// The three-dot menu in the case detail header. Houses the "more actions"
// that don't fit on the main action row: copy a shareable URL, copy the
// case ID for pasting into Slack/email/Zoho, jump to the raw audit JSON
// tab, and download a single-case xlsx (handy when you want to send Bettina
// just one row instead of the whole queue).
function CaseMenu({ caseId, onJumpToRaw }: { caseId: string; onJumpToRaw: () => void }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const numeric = caseId.replace(/^CASE-/, '');
  const link = `${window.location.origin}/?case=${numeric}`;

  const copy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      setTimeout(() => setCopied(null), 1200);
    } catch {
      // Fallback for clipboard API failures
      setCopied('Copy failed');
      setTimeout(() => setCopied(null), 1200);
    }
  };

  const exportXlsx = async () => {
    setOpen(false);
    const res = await fetch(`/api/cases/export.xlsx?ids=${numeric}`, { credentials: 'include' });
    if (!res.ok) { alert('Export failed'); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${runPrefixFrom(res)}${caseId}-${new Date().toISOString().slice(0, 10)}.xlsx`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ position: 'relative' }}>
      <button className="btn btn-sm btn-ghost" onClick={() => setOpen((v) => !v)} title="More actions">
        <Icon name="dot3" size={14} />
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 1100 }} />
          <div style={{
            position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 1101,
            background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6,
            minWidth: 220, padding: 4, boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
          }}>
            <MenuItem icon="ext" label="Copy case URL" onClick={() => copy(link, 'url')} sub={copied === 'url' ? 'Copied!' : null} />
            <MenuItem icon="doc" label={`Copy ${caseId}`} onClick={() => copy(caseId, 'id')} sub={copied === 'id' ? 'Copied!' : null} />
            <MenuItem icon="download" label="Download this case (.xlsx)" onClick={exportXlsx} />
            <MenuItem icon="doc" label="Jump to raw audit JSON" onClick={() => { setOpen(false); onJumpToRaw(); }} />
          </div>
        </>
      )}
    </div>
  );
}

function MenuItem({ icon, label, sub, onClick }: { icon: Parameters<typeof Icon>[0]['name']; label: string; sub?: string | null; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 10px',
        background: 'transparent', border: 'none', cursor: 'pointer',
        textAlign: 'left', fontSize: 12.5, color: 'var(--text)', borderRadius: 4,
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--surface-2)')}
      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
    >
      <Icon name={icon} size={13} />
      <span style={{ flex: 1 }}>{label}</span>
      {sub && <span className="c-ok" style={{ fontSize: 11 }}>{sub}</span>}
    </button>
  );
}

interface CaseDetailProps {
  caseObj: Case;
  userRole: 'admin' | 'reviewer';
  // 'drawer' = sliding side panel over the queue/dashboard (in-app navigation).
  // 'page'   = full-width canvas (new-tab links and ?case=N deep-links). Same
  //            inner content; just no backdrop and no narrow aside wrapper.
  display?: 'drawer' | 'page';
  onClose: () => void;
  onResolve: (reason: string) => Promise<void>;
  // Mark reviewed (SURFACED), Mark acknowledged (ACKD), Confirm violation.
  onTransition: (to: 'SURFACED' | 'ACKD' | 'CONFIRMED_VIOLATION', reason: string) => Promise<void>;
  onDismiss: (reason: string) => Promise<void>;
  onSuppress: (reason: string, expiresAt?: string | null) => Promise<void>;
  onReopen: () => void;
  // Notification-only callback for sub-flows that POST their own
  // transition (e.g. ExceptionLedgerSearch "Apply & resolve"). Tells the
  // parent the case state changed so it can close + refresh.
  onCaseChanged: () => void;
  // Refresh the case without closing it (SPC "doesn't cover": Bettina,
  // 2026-11-03 — there may be more contracts to review on the same case).
  onCaseRefresh?: () => void;
}

type Tab = 'overview' | 'visits' | 'payers' | 'caregivers' | 'financial' | 'notifications' | 'fbc' | 'history' | 'raw';

export function CaseDetail({ caseObj, userRole, display = 'drawer', onClose, onResolve, onTransition, onDismiss, onSuppress, onReopen, onCaseChanged, onCaseRefresh }: CaseDetailProps) {
  // Always re-fetch the case state when the drawer opens. The parent's
  // cases array is loaded once per session and can be stale (e.g. an audit
  // run reopened a DISMISSED case, or another reviewer changed state in a
  // different tab). We trust /api/cases/:id over the prop for the
  // state-driven action UI.
  const [freshState, setFreshState] = useState<Case['state'] | null>(null);
  const [stateHistory, setStateHistory] = useState<HistoryEntry[]>([]);
  useEffect(() => {
    const num = caseObj.id.replace(/^CASE-/, '').replace(/^0+/, '');
    api<{ case: { state: Case['state'] }; history?: HistoryEntry[] }>(`/api/cases/${num}`)
      .then((d) => { setFreshState(d.case.state); setStateHistory(d.history ?? []); })
      .catch(() => setFreshState(null));
  }, [caseObj.id]);
  const c: Case = { ...caseObj, ...(freshState ? { state: freshState } : {}) };
  const [tab, setTab] = useState<Tab>('overview');
  // The four state-changing actions all route through ConfirmActionModal
  // (instead of firing immediately) so reviewers see exactly what will
  // happen and can attach a reason. Per-action copy lives in
  // ACTION_DIALOGS below.
  const [pending, setPending] = useState<keyof typeof ACTION_DIALOGS | null>(null);
  const ACTION_DIALOGS = {
    review: {
      title: 'Mark reviewed',
      body: <>Moves the case to <b>Reviewed</b>: you have reviewed it and it is ready to notify. This is <b>not</b> a finding of violation; that is decided after the franchise partner responds. Send the notice from the case list (<b>Send notices</b>) or this case's Notifications tab.</>,
      tone: 'warn' as const, confirmLabel: 'Mark reviewed', reasonField: 'optional' as const, expiresField: false,
      onConfirm: (reason: string) => onTransition('SURFACED', reason),
    },
    ack: {
      title: 'Mark acknowledged',
      body: <>The franchise partner has responded to the notice. Next: <b>Confirm violation</b> if there is no arrangement, or <b>Mark resolved</b> if there is one (SPC signed, or the tool had it wrong).</>,
      tone: 'warn' as const, confirmLabel: 'Mark acknowledged', reasonField: 'optional' as const, expiresField: false,
      onConfirm: (reason: string) => onTransition('ACKD', reason),
    },
    confirm: {
      title: 'Confirm violation',
      body: <>The partner has confirmed there is <b>no arrangement</b> covering this service{c.state === 'NOTIFIED' ? <> — or has not responded (the history will note that Acknowledged was skipped)</> : null}. The case becomes a <b>Confirmed violation</b> and stays open until you mark it Resolved.</>,
      tone: 'danger' as const, confirmLabel: 'Confirm violation', reasonField: 'optional' as const, expiresField: false,
      onConfirm: (reason: string) => onTransition('CONFIRMED_VIOLATION', reason),
    },
    resolve: {
      title: 'Mark resolved',
      body: <>Closes this case as <b>RESOLVED</b> (we handled it — talked to franchisee, billing corrected, out of scope, etc.). Terminal. Future audits will not re-flag this triple.</>,
      tone: 'ok' as const, confirmLabel: 'Mark resolved', reasonField: 'optional' as const, expiresField: false,
      onConfirm: onResolve,
    },
    dismiss: {
      title: 'Dismiss case',
      body: <>Closes this case as a <b>one-time false positive</b>. The next audit will <b>re-evaluate</b> this (client × franchisee × zip) triple and may re-open the case if conditions recur. Use <b>Suppress</b> instead if you want a permanent skip.</>,
      tone: 'warn' as const, confirmLabel: 'Dismiss', reasonField: 'optional' as const, expiresField: false,
      onConfirm: onDismiss,
    },
    suppress: {
      title: 'Suppress this triple',
      body: <>Closes the case AND writes a <b>suppression record</b> for (this client × <span className="mono">{c.franchiseeAf.replace(/\.\d+$/, '')}</span> stem × zip <span className="mono">{c.dominantZip}</span>). The audit engine skips this triple while the suppression is active. To make it <b>temporary</b> — e.g. servicing permitted for 3 months, or until the neighboring office is credentialed — set the "Resolved until" date below; when it passes, the case re-opens automatically if the situation persists. Leave the date empty for a permanent skip. Reason is required.</>,
      tone: 'warn' as const, confirmLabel: 'Suppress', reasonField: 'required' as const, expiresField: true,
      onConfirm: onSuppress,
    },
  };

  // Wrapper differs by display mode but the inner header/tabs/body markup is shared.
  // page mode: standalone full-canvas layout (own scroll). drawer mode: sliding aside
  // overlaid on whatever's behind, with backdrop to dismiss.
  const Wrapper = display === 'page'
    ? ({ children }: { children: ReactNode }) => (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, background: 'var(--surface)' }}>{children}</div>
      )
    : ({ children }: { children: ReactNode }) => (
        <>
          <div className="drawer-backdrop" onClick={onClose} />
          <aside className="drawer">{children}</aside>
        </>
      );

  return (
    <Wrapper>
        <div style={{ padding: '14px 20px 12px', borderBottom: '1px solid var(--border)', background: 'var(--surface)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: `var(--${c.sev === 'ok' ? 'ok' : c.sev})` }} />
            <span className="mono" style={{ fontSize: 13, fontWeight: 600 }}>{c.id}</span>
            <StatusPill state={c.state} />
            {(c.clientStatus || c.clientActive != null) && (
              <span className="pill" style={{ height: 20, fontSize: 10.5 }} title="AxisCare client status (active switch · status label)">
                <span className={`pill-dot dot-${c.clientActive ? 'ok' : 'warn'}`}></span>
                {c.clientActive == null ? 'Status unknown' : c.clientActive ? 'Active' : 'Inactive'}
                {c.clientStatus && !new RegExp(`^${c.clientActive ? 'active' : 'inactive'}$`, 'i').test(c.clientStatus) ? ` · ${c.clientStatus}` : ''}
              </span>
            )}
            {c.zipOnlyTag && <span className="pill" style={{ height: 20, fontSize: 10.5 }}>ZIP-ONLY</span>}
            <span className="muted" style={{ fontSize: 12 }}>· opened {formatRelative(c.openedAt)}{c.audit.triggeredBy ? ` · ${c.audit.triggeredBy}` : ''}</span>
            <div style={{ flex: 1 }} />
            <a
              className="btn btn-sm btn-ghost"
              href={`${window.location.origin}/?case=${c.id.replace(/^CASE-/, '')}`}
              target="_blank"
              rel="noreferrer"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none' }}
              title="Open this case in a new browser tab — useful for side-by-side review"
            >
              <Icon name="ext" size={13} /> Open in new tab
            </a>
            <CaseMenu caseId={c.id} onJumpToRaw={() => setTab('raw')} />
            <button className="btn btn-sm btn-ghost btn-icon" onClick={onClose}><Icon name="close" size={14} /></button>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16 }}>
            <div>
              <h2 style={{ margin: 0, fontSize: 19, fontWeight: 600, letterSpacing: '-0.012em' }}>
                {c.patient}
                {c.legacy && (
                  <span className="pill" style={{ height: 20, fontSize: 10.5, marginLeft: 8, verticalAlign: 'middle', color: 'var(--ok)', borderColor: 'color-mix(in oklch, var(--ok) 45%, var(--border))', background: 'var(--ok-soft)' }}
                      title={`Legacy client — grandfathered: ${c.legacy.reason} No break in service voided it.`}>
                  <span className="pill-dot dot-ok"></span>Legacy
                </span>
                )}
              </h2>
              <div className="muted" style={{ fontSize: 12.5, marginTop: 2 }}>
                <span className="mono">{c.axiscareClientId}</span> · {c.location} · servicing
                {' '}<span className="mono" style={{ color: 'var(--text)' }}>{c.franchiseeAf}</span>
                {' '}<span className="faint">({c.franchiseeName})</span>
                {c.ownerAf && <> · territory of <span className="mono c-warn">{c.ownerAf}</span> <span className="faint">({c.ownerName})</span></>}
              </div>
              <NoticeTimeline history={stateHistory} />
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <AssignButton caseId={c.id} currentAssignee={c.assignee} />
              {/* Closed cases (RESOLVED/DISMISSED) are terminal for non-admins.
                  Admins see a Reopen button that walks state back to NEW with
                  a required reason — both the original closure and the reopen
                  are preserved in case_state_history + admin_actions. */}
              {(c.state === 'RESOLVED' || c.state === 'DISMISSED' || c.state === 'CONFIRMED_VIOLATION') ? (
                <>
                  {c.state === 'CONFIRMED_VIOLATION' && (
                    <button className="btn btn-ok" onClick={() => setPending('resolve')}><Icon name="check" size={13} /> Mark resolved</button>
                  )}
                  {userRole === 'admin' ? (
                    <button className="btn" onClick={onReopen} title="Admin only — reopens this case back to NEW with an audited reason">
                      <Icon name="refresh" size={13} /> Reopen case
                    </button>
                  ) : (
                    <span className="muted" style={{ fontSize: 12, padding: '0 8px', display: 'flex', alignItems: 'center' }}>
                      {c.state === 'CONFIRMED_VIOLATION' ? 'Confirmed · admin can reopen' : 'Closed · admin can reopen'}
                    </span>
                  )}
                </>
              ) : c.state === 'CLEARED_BY_EXCEPTION' ? (
                <span className="muted" style={{ fontSize: 12, padding: '0 8px', display: 'flex', alignItems: 'center' }}>
                  Cleared by SPC/IR/FA · the audit reopens it if the grant stops covering it
                </span>
              ) : (
                <>
                  {(c.state === 'NEW' || c.state === 'SURFACED') && (
                    <button className="btn btn-ghost" onClick={() => setPending('suppress')} title="Close + permanently skip future audits for this triple">
                      <Icon name="shield" size={13} /> Suppress
                    </button>
                  )}
                  <button className="btn btn-ghost" onClick={() => setPending('dismiss')} title="Close as one-time false positive (may re-open on next audit)">
                    <Icon name="close" size={13} /> Dismiss
                  </button>
                  {c.state === 'NEW' && (
                    <button className="btn btn-primary" onClick={() => setPending('review')}><Icon name="check" size={13} /> Mark reviewed</button>
                  )}
                  {c.state === 'NOTIFIED' && (
                    <button className="btn" onClick={() => setPending('ack')}><Icon name="bell" size={13} /> Mark acknowledged</button>
                  )}
                  {(c.state === 'NOTIFIED' || c.state === 'ACKD') && (
                    <button className="btn btn-danger" onClick={() => setPending('confirm')}><Icon name="alert" size={13} /> Confirm violation</button>
                  )}
                  <button className="btn btn-ok" onClick={() => setPending('resolve')}><Icon name="check" size={13} /> Mark resolved</button>
                </>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 0, marginTop: 14, borderBottom: '1px solid transparent', marginBottom: -12 }}>
            {([
              ['overview', 'Overview', c.flags.length],
              ['visits', 'Visits & GPS', c.visits],
              ['payers', 'Payer × zip', null],
              ['financial', 'Financial', null],
              ['caregivers', 'Caregivers', c.caregivers],
              ['notifications', 'Notifications', null],
              ['fbc', 'FBC updates', null],
              ['history', 'Activity', null],
              ['raw', 'Raw audit JSON', null],
            ] as const).map(([id, label, count]) => {
              const active = tab === id;
              return (
                <div key={id} onClick={() => setTab(id as Tab)} style={{
                  padding: '8px 14px',
                  fontSize: 13,
                  fontWeight: active ? 600 : 500,
                  color: active ? 'var(--text)' : 'var(--text-muted)',
                  borderBottom: `2px solid ${active ? 'var(--accent)' : 'transparent'}`,
                  cursor: 'default',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                  {label}
                  {count != null && <span style={{ fontSize: 11, padding: '1px 6px', background: 'var(--surface-3)', borderRadius: 8, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>{count}</span>}
                </div>
              );
            })}
          </div>
        </div>

        <div className="scroll-y" style={{ flex: 1, padding: 20, background: 'var(--bg)' }}>
          {tab === 'overview' && <OverviewTab c={c} onCaseChanged={onCaseChanged} onCaseRefresh={onCaseRefresh} />}
          {tab === 'visits' && <VisitsTab c={c} />}
          {tab === 'payers' && <PayersTab c={c} />}
          {tab === 'financial' && <FinancialTab c={c} />}
          {tab === 'caregivers' && <CaregiversTab c={c} />}
          {tab === 'notifications' && <CaseNotifications caseId={c.id} caseState={c.state} onCaseChanged={onCaseChanged} />}
          {tab === 'fbc' && <CaseFbcUpdates caseId={c.id} />}
          {tab === 'history' && <HistoryTab c={c} />}
          {tab === 'raw' && <RawTab c={c} />}
        </div>
      {pending && (
        <ConfirmActionModal
          title={ACTION_DIALOGS[pending].title}
          body={ACTION_DIALOGS[pending].body}
          tone={ACTION_DIALOGS[pending].tone}
          confirmLabel={ACTION_DIALOGS[pending].confirmLabel}
          reasonField={ACTION_DIALOGS[pending].reasonField}
          expiresField={ACTION_DIALOGS[pending].expiresField}
          onConfirm={ACTION_DIALOGS[pending].onConfirm}
          onClose={() => setPending(null)}
        />
      )}
    </Wrapper>
  );
}

// Renders the evidence line for a flag from the REAL details payload the
// audit engine stored on the case (c.flagDetails). No invented values: any
// field the engine didn't record renders as an em dash. (The previous version
// interpolated hardcoded demo copy — "zip 60611", "8.4mi apart", fake dates —
// which surfaced as phantom data to reviewers.)
function FlagEvidence({ flagKey, c }: { flagKey: FlagKind; c: Case }) {
  const d = c.flagDetails?.[flagKey] ?? {};
  const s = (k: string): string | null => {
    const v = (d as Record<string, unknown>)[k];
    return v == null ? null : String(v);
  };
  const pct = (k: string): string | null => {
    const v = (d as Record<string, unknown>)[k];
    return typeof v === 'number' ? `${(v * 100).toFixed(0)}%` : null;
  };
  const ev: Record<FlagKind, ReactNode> = {
    violation_candidate: <>survived all gates · dominant zip <span style={{ color: 'var(--text)' }}>{c.dominantZip}</span> owned by <span className="mono">{c.ownerAf ?? '—'}</span> · no SPC, no IR matched · awaiting human confirmation</>,
    split_care: <>top zip {c.dominantZip} holds <span style={{ color: 'var(--text)' }}>{pct('share') ?? '—'}</span> of {c.visits} visits across <span className="c-warn">{s('distinctZips') ?? '—'} zips</span> · no zip reached the 85% dominance threshold</>,
    high_manual_rate: <>{(c.manualRate * 100).toFixed(0)}% of {c.visits} clock-ins were manual · GPS available for only {Math.round(c.visits * (1 - c.manualRate))} visits · threshold 50%</>,
    cross_zip_mismatch: <>dominant clock-in zip <span style={{ color: 'var(--text)' }}>{s('dominantZip') ?? c.dominantZip}</span> · client record zip <span style={{ color: 'var(--text)' }}>{s('recordZip') ?? '—'}</span> · zips disagree and at least one falls outside <span className="mono">{c.franchiseeAf}</span> stem territory</>,
    break_in_service: <>longest gap between visits in window: <span className="c-warn">{c.longestGapDays != null ? `${c.longestGapDays} days` : '—'}</span> · a ≥30-day gap after the owner's effective date voids grandfather</>,
    address_tampering: <>listed street address geocodes to zip <span style={{ color: 'var(--text)' }}>{s('geocodedZip') ?? '—'}</span> · zip on the AxisCare record is <span className="c-danger">{s('recordZip') ?? '—'}</span></>,
    geocode_tampering: <>AxisCare&apos;s stored geocode falls in zip <span className="c-danger">{s('storedGeocodeZip') ?? '—'}</span> · the listed street address geocodes to zip <span style={{ color: 'var(--text)' }}>{s('addressGeocodeZip') ?? '—'}</span> · stored pin likely edited by hand</>,
    zip_mismatch: <>client record zip <span style={{ color: 'var(--text)' }}>{s('recordZip') ?? '—'}</span> differs from dominant clock-in zip {c.dominantZip} · could be a border-of-zip artifact</>,
    spc_scope_review: <>signed grant from <span className="mono">{s('grantingAf') ?? '—'}</span> scoped to <span style={{ color: 'var(--text)' }}>&quot;{s('scopeValue') ?? '—'}&quot;</span> ({s('scopeKind') ?? 'payer'}) · couldn&apos;t be verified against this pair&apos;s visit payers · confirm manually{s('sourceArtifactId') ? <> · envelope <span className="mono">{s('sourceArtifactId')}</span></> : null}</>,
    placeholder_client: <>client name contains <span style={{ color: 'var(--text)' }}>{((c.flagDetails?.placeholder_client?.words as string[] | undefined) ?? []).join(', ') || '—'}</span> · possibly an office, training or caregiver record · mark &quot;Not a real client&quot; or &quot;Real client&quot;</>,
    client_status_unknown: <>the warehouse has no AxisCare active/inactive flag for this client{s('status') ? <> (label <span style={{ color: 'var(--text)' }}>{s('status')}</span>)</> : null} · audits cover active clients only, so it was kept rather than dropped · check the client in AxisCare</>,
  };
  return <>{ev[flagKey]}</>;
}

// QN (Bettina, 2026-10-21): the client name looks like an office / training /
// caregiver record. One click closes the case and suppresses it for the
// servicing franchisee in every zip.
function NotAClientBanner({ c, onDone }: { c: Case; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const words = (c.flagDetails?.placeholder_client?.words as string[] | undefined) ?? [];
  const mark = async () => {
    const note = prompt(`Mark ${c.patient} as NOT a real client?\n\nThis closes the case and suppresses it for ${c.franchiseeAf.replace(/\.\d+$/, '')} in every zip, so future audits won't alert on it. Optional note:`);
    if (note === null) return;
    setBusy(true); setError(null);
    try {
      await api('/api/cases/not-a-client', { method: 'POST', body: { ids: [Number(c.id.replace(/^CASE-/, '').replace(/^0+/, ''))], note: note.trim() || null } });
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    } finally {
      setBusy(false);
    }
  };
  const real = async () => {
    const note = prompt(`Mark ${c.patient} as a REAL client?\n\nThe "Possible non-client" flag is removed from this client's cases, and future audits stop raising it. The case stays open. Optional note:`);
    if (note === null) return;
    setBusy(true); setError(null);
    try {
      await api('/api/cases/real-client', { method: 'POST', body: { ids: [Number(c.id.replace(/^CASE-/, '').replace(/^0+/, ''))], note: note.trim() || null } });
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="card card-pad" style={{ background: 'var(--warn-soft)', border: '1px solid color-mix(in oklch, var(--warn) 35%, var(--border))', fontSize: 12.5, display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{ flex: 1 }}>
        <strong>Possible non-client.</strong> The name contains {words.length ? words.map((w) => `"${w}"`).join(', ') : 'a placeholder word'} — this may be
        an office, training or caregiver record rather than a real client. If so, mark it so future audits stop alerting on it for this servicing franchisee.
        {error && <div className="c-warn" style={{ marginTop: 4 }}>{error}</div>}
      </div>
      <button className="btn btn-sm" onClick={real} disabled={busy} title="Remove the flag; audits stop raising it for this client">{busy ? 'Saving…' : 'Real client'}</button>
      <button className="btn btn-sm" onClick={mark} disabled={busy}>{busy ? 'Saving…' : 'Not a real client'}</button>
    </div>
  );
}

function OverviewTab({ c, onCaseChanged, onCaseRefresh }: { c: Case; onCaseChanged: () => void; onCaseRefresh?: () => void }) {
  const [mapExpanded, setMapExpanded] = useState(false);
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 16 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {c.flags.includes('placeholder_client') && !['DISMISSED', 'RESOLVED'].includes(c.state) && (
          <NotAClientBanner c={c} onDone={onCaseChanged} />
        )}
        {c.ownerNotYetOpen && c.ownerAf && (
          // Pre-2026-FDD territories are protected from signing, before they
          // open; servicing there is encroachment unless permitted (2026-10-19).
          <div className="card card-pad" style={{ background: 'var(--info-soft)', border: '1px solid color-mix(in oklch, var(--info) 30%, var(--border))', fontSize: 12.5 }}>
            <strong>Territory owner not open yet.</strong>{' '}
            <span className="mono">{c.ownerAf}</span> is <strong>{c.ownerStatus}</strong>
            {c.ownerFddEra === 'pre2026'
              ? <> and signed under a pre-2026 FDD, so its territory is protected from signing.</>
              : <> and its FDD era is unknown in Zoho (no original agreement date or FDD year), so it is treated as protected; check the Zoho record.</>}
            {' '}<strong>Recommendation:</strong> an SPC from <span className="mono">{c.ownerAf}</span> granting{' '}
            <span className="mono">{c.franchiseeAf}</span> permission to serve here until <span className="mono">{c.ownerAf}</span> opens its doors,
            so this service is not encroachment.
          </div>
        )}
        <div className="card">
          <div className="sec-head"><h3>Flags · {c.flags.length}</h3></div>
          {c.flags.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center' }}>
              <Icon name="check" size={20} color="var(--ok)" />
              <div style={{ marginTop: 8, fontSize: 13, fontWeight: 500 }}>No flags raised</div>
              <div className="muted" style={{ fontSize: 12 }}>This (P × F) pair passed every gate in the v0.6 decision tree.</div>
            </div>
          ) : (
            <div style={{ padding: '4px 0' }}>
              {c.flags.map((f, i) => {
                const ft = FLAG_TYPES[f];
                return (
                  <div key={f} style={{ padding: '14px 16px', borderBottom: i < c.flags.length - 1 ? '1px solid var(--border)' : 'none', display: 'flex', gap: 12 }}>
                    <div style={{
                      width: 28, height: 28, borderRadius: 6,
                      background: `var(--${ft.sev}-soft)`, color: `var(--${ft.sev})`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      <Icon name={ft.sev === 'danger' ? 'alert' : ft.sev === 'warn' ? 'flag' : 'eye'} size={14} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 600, fontSize: 13.5 }}>{ft.label}</span>
                        <span className="badge" style={{ background: `var(--${ft.sev}-soft)`, color: `var(--${ft.sev})` }}>{ft.sev}</span>
                        <span className="muted mono" style={{ fontSize: 11 }}>{ft.ruleId}</span>
                      </div>
                      <div className="muted" style={{ fontSize: 12.5, marginTop: 3 }}>{ft.desc}</div>
                      <div style={{ marginTop: 8, padding: 10, background: 'var(--surface-2)', borderRadius: 5, fontSize: 12, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', lineHeight: 1.6 }}>
                        <FlagEvidence flagKey={f} c={c} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="card">
          <div className="sec-head" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <h3>Visit map · 90-day window</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span className="faint" style={{ fontSize: 11 }}>{c.visits} visits · {c.location}</span>
              <button
                className="btn btn-ghost btn-sm btn-icon"
                title="Pop out"
                aria-label="Pop out map"
                onClick={() => setMapExpanded(true)}
              >
                <Icon name="ext" size={13} />
              </button>
            </div>
          </div>
          <div style={{ padding: 12 }}><VisitMap c={c} /></div>
        </div>
      </div>
      {mapExpanded && <VisitMapModal c={c} onClose={() => setMapExpanded(false)} />}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div className="card card-pad">
          <div style={{ fontSize: 11.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600, marginBottom: 10 }}>Summary</div>
          <Stat label="Total revenue" value={formatCents(c.totalRevenueCents)} />
          <Stat label="Hours" value={c.hours.toFixed(1)} />
          <Stat label="Visits" value={c.visits} />
          <Stat label="Caregivers" value={c.caregivers} />
          <Stat label="FBC (servicing)" value={c.servicingFbcName
            ? <span title={c.servicingFbcEmail ?? undefined}>{c.servicingFbcName}</span>
            : <span className="muted">—</span>} />
          <Stat label="FBC (owner)" value={c.ownerFbcName
            ? <span title={c.ownerFbcEmail ?? undefined}>{c.ownerFbcName}</span>
            : <span className="muted">—</span>} />
          <Stat label="Manual rate" value={`${(c.manualRate * 100).toFixed(0)}%`} />
          <Stat
            label="Longest gap (days)"
            value={c.longestGapDays == null
              ? <span className="muted">—</span>
              : <span className={c.longestGapDays >= 30 ? 'c-warn' : ''}>{c.longestGapDays}</span>}
          />
          <div className="divider-h" style={{ margin: '10px 0' }} />
          <Stat label="Audit run" value={<span className="mono">{c.audit.runId ?? '—'}</span>} />
          <Stat label="Spec version" value={<span className="mono">{c.audit.specVersion ?? '—'}</span>} />
          <Stat label="Runtime" value={c.audit.runtimeMs != null ? `${(c.audit.runtimeMs / 1000).toFixed(1)}s` : '—'} />
        </div>

        <div className="card">
          <div className="sec-head"><h3>Payer split</h3></div>
          <div style={{ padding: 12 }}>
            <PayerBar split={c.payerSplit} />
            <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {Object.entries(c.payerSplit).map(([k, v]) => (
                <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: payerColor(c.payerSplit, k) }} />
                  <span style={{ flex: 1 }}>{k}</span>
                  <span className="num mono">{v === 0 ? '<1' : v}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="card card-pad">
          <div style={{ fontSize: 11.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600, marginBottom: 10 }}>Grandfather check</div>
          <Stat label="Client start of care" value={c.startOfCare ? c.startOfCare.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'} />
          <Stat label="Owner effective date" value={c.ownerEffectiveDate ? c.ownerEffectiveDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'} />
          {c.ownerEffectiveDateSource && (
            <div className="muted" style={{ fontSize: 11, margin: '-2px 0 6px' }}>from {c.ownerEffectiveDateSource}</div>
          )}
          <Stat
            label={c.daysGrandfather == null ? 'Grandfather gap' : c.daysGrandfather < 0 ? 'Grandfathered by' : 'Came after by'}
            value={c.daysGrandfather == null ? '—' : `${Math.abs(c.daysGrandfather)} days`}
          />
        </div>

        <div className="card card-pad">
          <div style={{ fontSize: 11.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600, marginBottom: 10 }}>Considered SPCs / IRs</div>
          {(c.state === 'DISMISSED' || c.state === 'RESOLVED') ? (
            <CaseResolutionDisplay caseId={c.id} state={c.state} />
          ) : (
            <>
              <ClearingException caseId={c.id} />
              <ExceptionLedgerSearch caseId={c.id} onResolved={onCaseChanged} onRefresh={onCaseRefresh} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * The SPC/IR the AUDIT ENGINE used to clear this case, if any.
 *
 * Previously this panel printed "No SPC or IR auto-matched" unconditionally,
 * so a case cleared by a permission contract showed no contract — the one
 * screen where verifying the grant matters most.
 *
 * Also warns where the grant's dates do not cover the visits it cleared. The
 * decision tree accepts a grant that merely OVERLAPS the audit window, so a
 * contract valid for a few days can clear a whole quarter of care.
 */
function ClearingException({ caseId }: { caseId: string }) {
  const [rows, setRows] = useState<ExceptionCandidate[] | null>(null);
  useEffect(() => {
    const num = caseId.replace(/^CASE-/, '');
    api<{ candidates: ExceptionCandidate[] }>(`/api/cases/${num}/exception-candidates`)
      .then((d) => setRows(d.candidates))
      .catch(() => setRows([]));
  }, [caseId]);

  if (rows === null) return null;
  const clearing = rows.filter((r) => r.cleared_by_engine);
  if (clearing.length === 0) {
    return (
      <div className="muted" style={{ fontSize: 12.5, marginBottom: 8 }}>
        No SPC or IR auto-matched. Manually attach one if matching missed it.
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 12 }}>
      {clearing.map((r) => {
        const cov = r.visit_coverage;
        const notCovered = cov ? cov.outside : 0;
        const noneCovered = cov ? cov.covered === 0 && cov.outside > 0 : false;
        return (
          <div key={r.id} className="card card-pad" style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span className="flag-pill sev-ok">
                {r.type === 'spc' ? 'SPC' : 'Internal referral'} cleared this case
              </span>
              <span className="mono" style={{ fontSize: 12.5 }}>
                {r.granting_af} → {r.receiving_af}
              </span>
              {!r.currently_valid && (
                <span className="flag-pill sev-warn">Expired</span>
              )}
            </div>
            <div className="muted" style={{ fontSize: 12.5, marginTop: 6 }}>
              Scope: {r.scope_kind}{r.scope_value ? ` · ${r.scope_value}` : ''}
              {' · '}Valid {r.valid_from ? String(r.valid_from).slice(0, 10) : '—'}
              {' to '}{r.valid_to ? String(r.valid_to).slice(0, 10) : 'open-ended'}
              {r.source_artifact_id ? <> · <span className="mono">{r.source_artifact_id}</span></> : null}
            </div>
            {notCovered > 0 && (
              <div className="flag-pill sev-danger" style={{ marginTop: 8, display: 'inline-block' }}>
                {noneCovered
                  ? `Covers NONE of the ${cov!.outside} visits audited — the grant's dates fall entirely outside them`
                  : `Covers ${cov!.covered} of ${cov!.covered + cov!.outside} visits audited · ${notCovered} outside its dates`}
              </div>
            )}
            {r.engine_reason && (
              <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>{r.engine_reason}</div>
            )}
          </div>
        );
      })}
    </div>
  );
}

interface ExceptionCandidate {
  id: number;
  type: 'spc' | 'internal_referral';
  granting_af: string;
  receiving_af: string;
  scope_kind: string;
  scope_value: string | null;
  valid_from: string | null;
  valid_to: string | null;
  both_signed: boolean;
  source_artifact_id: string | null;
  currently_valid: boolean;
  auto_match_gap: string;
  matchable: boolean;
  cleared_by_engine?: boolean;
  engine_reason?: string | null;
  visit_coverage?: { covered: number; outside: number };
  matched: {
    id: number; verdict: 'covers' | 'does_not_cover';
    created_at: string; reason: string | null; matched_by: string | null;
  } | null;
}

// Read-only summary of how a closed case (DISMISSED / RESOLVED) was decided.
// Pulls the latest closing transition from case_state_history and shows the
// actor + reason instead of the old hardcoded "Bettina S. · SPC-2241" mock.
function CaseResolutionDisplay({ caseId, state }: { caseId: string; state: 'DISMISSED' | 'RESOLVED' }) {
  const [latest, setLatest] = useState<HistoryEntry | null | undefined>(undefined);
  useEffect(() => {
    const num = caseId.replace(/^CASE-/, '').replace(/^0+/, '');
    api<{ history: HistoryEntry[] }>(`/api/cases/${num}`)
      .then((d) => {
        const closing = (d.history ?? []).slice().reverse().find((h) => h.to_state === state) ?? null;
        setLatest(closing);
      })
      .catch(() => setLatest(null));
  }, [caseId, state]);

  if (latest === undefined) return <div className="muted" style={{ fontSize: 12 }}>Loading…</div>;
  if (!latest) return <div className="muted" style={{ fontSize: 12 }}>No transition record found for {state.toLowerCase()} state.</div>;
  const actor = latest.actor_name ?? latest.actor_email ?? `user #${latest.actor_user_id ?? '?'}`;
  const initials = actor.split(/[\s@]/).map((p) => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <div className="avatar" style={{ width: 22, height: 22, fontSize: 10 }}>{initials}</div>
        <div style={{ fontSize: 12.5 }}>
          <span style={{ fontWeight: 500 }}>{actor}</span>
          <span className="muted"> · {state === 'RESOLVED' ? 'resolved the case' : 'dismissed the case'}</span>
        </div>
      </div>
      {latest.reason && <div className="muted" style={{ fontSize: 12, whiteSpace: 'pre-wrap' }}>{latest.reason}</div>}
      <div className="faint mono" style={{ fontSize: 11 }}>{timeAgo(latest.created_at)}</div>
    </div>
  );
}

// Manual SPC ↔ client matcher (Bettina's 7/20 ask). Lists every permission
// contract written for this case's owner→servicing pair, explains why each one
// didn't clear the case automatically, and lets the reviewer confirm "yes,
// this contract is for this client" — which the audit engine then honors on
// every future run, so the case doesn't come back.
function ExceptionLedgerSearch({ caseId, onResolved, onRefresh }: { caseId: string; onResolved: () => void; onRefresh?: () => void }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<ExceptionCandidate[] | null>(null);
  const [clientName, setClientName] = useState<string | null>(null);
  const [applying, setApplying] = useState<number | null>(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ text: string; tone: 'muted' | 'c-ok' | 'c-warn' } | null>(null);

  const load = () => {
    const num = caseId.replace(/^CASE-/, '');
    api<{ candidates: ExceptionCandidate[]; client: { name: string | null } }>(`/api/cases/${num}/exception-candidates`)
      .then((d) => { setRows(d.candidates); setClientName(d.client?.name ?? null); })
      .catch(() => setRows([]));
  };
  useEffect(() => { if (open && !rows) load(); }, [open, rows, caseId]);

  const match = async (r: ExceptionCandidate) => {
    if (!r.matchable) return;
    setApplying(r.id);
    setError(null);
    try {
      const num = caseId.replace(/^CASE-/, '');
      await api(`/api/cases/${num}/spc-matches`, {
        method: 'POST',
        body: { exception_ledger_id: r.id, verdict: 'covers', reason: note.trim() || undefined, resolve_case: true },
      });
      setOpen(false);
      onResolved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed');
    } finally {
      setApplying(null);
    }
  };

  // Mirror of match(): rule the contract OUT for this client. The engine stops
  // offering it, so the same question isn't re-asked on every future run.
  // Stays on the case with this list open (Bettina, 2026-11-03): a case can
  // have several contracts to review, and the case refreshes behind it.
  const reject = async (r: ExceptionCandidate) => {
    const label = r.type === 'spc' ? 'SPC' : 'internal referral';
    const othersLeft = (rows ?? []).filter((x) => x.id !== r.id && !x.matched).length;
    let alsoConfirm = false;
    if (othersLeft > 0) {
      // More to review: never offer "mark Reviewed" yet, and let Cancel back out.
      if (!window.confirm(
        `Record that this ${label} does NOT cover this client?\n\n`
        + 'Future audit runs will stop offering it, and the rejection is saved to the case file.\n\n'
        + `${othersLeft} other contract(s) on this case still to review; the case stays open.`,
      )) return;
    } else {
      alsoConfirm = window.confirm(
        `Record that this ${label} does NOT cover this client?\n\n`
        + 'Future audit runs will stop offering it, and the rejection is saved to the case file.\n\n'
        + 'This is the last contract on the case to review.\n'
        + 'OK = also mark this case Reviewed (ready to notify — not a confirmed violation).\n'
        + 'Cancel = just record the rejection and leave the case where it is.',
      );
    }
    setApplying(r.id);
    setError(null);
    setNotice(null);
    try {
      const num = caseId.replace(/^CASE-/, '');
      const resp = await api<{ recheck?: { applied: boolean; outcome: string | null; decidedBy: string | null; reason: string } }>(`/api/cases/${num}/spc-matches`, {
        method: 'POST',
        body: {
          exception_ledger_id: r.id, verdict: 'does_not_cover',
          reason: note.trim() || undefined, confirm_violation: alsoConfirm,
        },
      });
      // The case is re-checked at once (2026-11-02): with no SPC/IR left to
      // review it moves on, e.g. to violation candidate.
      const rc = resp.recheck;
      const movedOn = !!rc?.applied && rc.decidedBy !== 'Q5_spc_review';
      const ruled = `Ruled out: ${r.granting_af} → ${r.receiving_af} ${label}.`;
      if (alsoConfirm) setNotice({ text: `${ruled} Case marked Reviewed.`, tone: 'c-ok' });
      else if (movedOn) setNotice({ text: `${ruled} Re-checked now: ${rc!.outcome === 'VIOLATION_CANDIDATE' ? 'this case is now a violation candidate' : `the case was updated (${rc!.decidedBy})`}. ${rc!.reason}`, tone: 'c-warn' });
      else if (rc) setNotice({ text: `${ruled} ${rc.applied ? `Re-checked now: still needs review — ${rc.reason}` : `Not re-checked: ${rc.reason}`}`, tone: 'muted' });
      else setNotice({ text: ruled, tone: 'muted' });
      setNote('');
      setRows(null); load();
      onRefresh?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed');
    } finally {
      setApplying(null);
    }
  };

  const unmatch = async (r: ExceptionCandidate) => {
    if (!r.matched) return;
    if (!confirm('Remove this match? Future audit runs will stop treating this contract as covering this client.')) return;
    setApplying(r.id);
    try {
      const num = caseId.replace(/^CASE-/, '');
      await api(`/api/cases/${num}/spc-matches/${r.matched.id}`, { method: 'DELETE' });
      setRows(null);
      load();
      onRefresh?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed');
    } finally { setApplying(null); }
  };

  return (
    <>
      <button className="btn btn-sm" style={{ width: '100%' }} onClick={() => setOpen(true)}>
        <Icon name="shield" size={12} /> Match permission contract
      </button>
      {open && (
        <div onClick={() => setOpen(false)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: 'var(--surface)', borderRadius: 8, padding: 18, width: 820, maxHeight: '84vh', overflow: 'auto',
            border: '1px solid var(--border)', position: 'relative', zIndex: 1001,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>Match a permission contract to this client</h3>
                <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                  AxisCare client: <b>{clientName ?? '(unknown)'}</b>
                </div>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => setOpen(false)}><Icon name="close" size={12} /></button>
            </div>
            <div className="muted" style={{ fontSize: 12, margin: '10px 0 14px' }}>
              These are the SPCs and internal referrals written from this territory owner to the servicing
              franchisee. If one of them covers this client but the names didn't line up automatically,
              confirm the match — the audit engine will honor it on every future run, so the case won't come back.
            </div>

            {!rows && <div className="muted">Loading…</div>}
            {rows && rows.length === 0 && (
              <div className="muted" style={{ fontSize: 12.5 }}>
                No SPC or IR rows in the ledger match this case's owner→servicing pair. If you know one exists, run System Health → Sync now on Exception ledger first.
              </div>
            )}

            {rows && rows.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {rows.map((r) => (
                  <div key={r.id} style={{
                    border: `1px solid ${r.matched ? (r.matched.verdict === 'covers' ? 'var(--ok)' : 'var(--warn)') : 'var(--border)'}`,
                    borderRadius: 8, padding: '10px 14px',
                    background: r.matched ? (r.matched.verdict === 'covers' ? 'var(--ok-soft)' : 'var(--warn-soft)') : 'var(--surface)',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <span className="mono" style={{ fontSize: 11.5, fontWeight: 600 }}>{r.type.toUpperCase()}</span>
                      <span className="mono" style={{ fontSize: 12 }}>{r.granting_af} → {r.receiving_af}</span>
                      <span style={{ fontSize: 12 }}>
                        <span className="faint">scope:</span> {r.scope_kind}{r.scope_value ? ` · ${r.scope_value}` : ''}
                      </span>
                      <span className="muted" style={{ fontSize: 11.5 }}>
                        valid through {r.valid_to ? parseDateOnly(r.valid_to)!.toLocaleDateString() : 'no expiry'}
                      </span>
                      {r.currently_valid
                        ? <span className="c-ok" style={{ fontSize: 11.5 }}>Active</span>
                        : <span className="c-warn" style={{ fontSize: 11.5 }}>{r.both_signed ? 'Expired' : 'Not fully executed'}</span>}
                      <div style={{ flex: 1 }} />
                      {r.matched ? (
                        <button className="btn btn-sm btn-ghost" disabled={applying != null} onClick={() => unmatch(r)}>
                          <Icon name="close" size={11} /> Undo {r.matched.verdict === 'covers' ? 'match' : 'rejection'}
                        </button>
                      ) : (
                        <>
                          <button
                            className="btn btn-sm btn-ghost"
                            disabled={applying != null}
                            title="Record that this contract does not cover this client — the tool stops asking on future runs"
                            onClick={() => reject(r)}
                          >
                            {applying === r.id ? 'Saving…' : "Doesn't cover this client"}
                          </button>
                          <button
                            className="btn btn-sm"
                            disabled={!r.matchable || applying != null}
                            title={r.matchable ? 'Confirm this contract covers this client and resolve the case' : r.auto_match_gap}
                            onClick={() => match(r)}
                          >
                            {applying === r.id ? 'Matching…' : 'This covers this client'}
                          </button>
                        </>
                      )}
                    </div>
                    {r.matched ? (
                      r.matched.verdict === 'covers' ? (
                        <div className="c-ok" style={{ fontSize: 11.5, marginTop: 6 }}>
                          ✓ Matched{r.matched.matched_by ? ` by ${r.matched.matched_by}` : ''} — future runs treat this client as covered.
                          {r.matched.reason ? ` "${r.matched.reason}"` : ''}
                        </div>
                      ) : (
                        <div className="c-warn" style={{ fontSize: 11.5, marginTop: 6 }}>
                          ✕ Ruled out{r.matched.matched_by ? ` by ${r.matched.matched_by}` : ''} — future runs will not offer this contract for this client.
                          {r.matched.reason ? ` "${r.matched.reason}"` : ''}
                        </div>
                      )
                    ) : (
                      <div className="muted" style={{ fontSize: 11.5, marginTop: 6 }}>
                        <b>Why it didn't apply automatically:</b> {r.auto_match_gap}
                      </div>
                    )}
                  </div>
                ))}

                <div style={{ marginTop: 4 }}>
                  <label className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Note (optional — recorded with the match)
                  </label>
                  <input
                    className="input"
                    style={{ width: '100%', marginTop: 4, fontSize: 12.5 }}
                    placeholder='e.g. "Confirmed with Dave Clifford — Jim is James Smith"'
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                </div>
              </div>
            )}

            {error && <div className="c-danger" style={{ fontSize: 12, marginTop: 10 }}>{error}</div>}
            {notice && <div className={notice.tone} style={{ fontSize: 12, marginTop: 10 }}>{notice.text}</div>}
            <div className="muted" style={{ fontSize: 11.5, marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
              Both decisions are remembered permanently, so the tool stops asking on future runs. Matching
              resolves the case; only fully-executed, in-date contracts can be matched — a match resolves a
              name ambiguity, it can't override a missing signature or an expired agreement. Ruling a contract
              out is always allowed and only makes the audit stricter.
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function PayerBar({ split }: { split: Case['payerSplit'] }) {
  const total = Object.values(split).reduce<number>((s, v) => s + (v ?? 0), 0) || 1;
  return (
    <div className="barlane" style={{ height: 12 }}>
      {Object.entries(split).map(([k, v]) => (
        <span key={k} style={{ background: payerColor(split, k), width: `${(v ?? 0) / total * 100}%` }} title={`${k} · ${v === 0 ? '<1' : v}%`} />
      ))}
    </div>
  );
}

function VisitMap({ c }: { c: Case }) {
  const [visits, setVisits] = useState<VisitDetail[] | null>(null);
  useEffect(() => {
    api<{ visits: VisitDetail[] }>(`/api/cases/${c.id.replace('CASE-', '').replace(/^0+/, '')}/visits`)
      .then((d) => setVisits(d.visits))
      .catch(() => setVisits([]));
  }, [c.id]);
  return (
    <div style={{ position: 'relative' }}>
      <LeafletMap visits={visits} dominantZip={c.dominantZip} height={280} />
      <div style={{
        position: 'absolute', top: 8, right: 8, padding: '3px 8px', background: 'var(--surface)',
        border: '1px solid var(--border)', borderRadius: 3, fontSize: 11, fontFamily: 'var(--font-mono)',
        color: 'var(--text-muted)', pointerEvents: 'none', zIndex: 400,
      }}>
        {c.ownerAf ? <>Owner of Zip: {c.ownerAf}</> : 'Owner of Zip: none'} · {c.dominantZip}
      </div>
    </div>
  );
}

function VisitMapModal({ c, onClose }: { c: Case; onClose: () => void }) {
  const [visits, setVisits] = useState<VisitDetail[] | null>(null);
  useEffect(() => {
    api<{ visits: VisitDetail[] }>(`/api/cases/${c.id.replace('CASE-', '').replace(/^0+/, '')}/visits`)
      .then((d) => setVisits(d.visits))
      .catch(() => setVisits([]));
  }, [c.id]);
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: 'var(--surface)', borderRadius: 8, padding: 14, width: '90vw', maxWidth: 1200, height: '85vh',
        border: '1px solid var(--border)', display: 'flex', flexDirection: 'column',
        position: 'relative', zIndex: 1001,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>{c.patient} · {visits?.length ?? '…'} visits</h3>
            <div className="muted mono" style={{ fontSize: 11.5, marginTop: 2 }}>{c.ownerAf ? <>Owner of Zip: {c.ownerAf}</> : 'Owner of Zip: none'} · {c.dominantZip} · {c.location}</div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><Icon name="close" size={12} /></button>
        </div>
        <div style={{ flex: 1, minHeight: 0 }}>
          <LeafletMap visits={visits} dominantZip={c.dominantZip} height="100%" />
        </div>
      </div>
    </div>
  );
}

// Leaflet wrapper. Refs the container div + map instance so we can mount the
// map once visits arrive, then update markers without remounting. Calls
// invalidateSize() after expand so the modal-sized map redraws tiles.
function LeafletMap({ visits, dominantZip, height }: { visits: VisitDetail[] | null; dominantZip: string; height: number | string }) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<LeafletNS.Map | null>(null);
  const layerRef = useRef<LeafletNS.LayerGroup | null>(null);
  const leafletRef = useRef<typeof import('leaflet') | null>(null);
  // Single state flag so render-effect re-runs once the async map init resolves —
  // avoids the race where visits arrive before the map is ready and we skip fitBounds.
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const L = (await import('leaflet')).default;
      if (cancelled || !containerRef.current || mapRef.current) return;
      leafletRef.current = L;
      const map = L.map(containerRef.current, { zoomControl: true, scrollWheelZoom: true });
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap',
        maxZoom: 19,
        // OSM's tile servers answer "403 Access blocked" to requests with no
        // Referer, and the site-wide Referrer-Policy is same-origin (server
        // index.ts). Let the tiles alone send our origin — never a path, so no
        // /reset?token= or case URL leaks.
        referrerPolicy: 'strict-origin-when-cross-origin',
      }).addTo(map);
      mapRef.current = map;
      layerRef.current = L.layerGroup().addTo(map);
      map.setView([39.8283, -98.5795], 4);
      setTimeout(() => map.invalidateSize(), 100);
      setMapReady(true);
    })();
    return () => {
      cancelled = true;
      mapRef.current?.remove();
      mapRef.current = null;
      layerRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!mapReady || !visits || !mapRef.current || !layerRef.current || !leafletRef.current) return;
    const L = leafletRef.current;
    layerRef.current.clearLayers();
    const points: [number, number][] = [];
    // Colour and label each clock-in / clock-out by who owns its zip, relative
    // to the SERVICING territory (server: GET /cases/:id/visits ownership).
    // Clock-in = filled dot, clock-out = hollow ring.
    const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;');
    for (const v of visits) {
      const start = new Date(v.start_time);
      const ends = [
        { kind: 'Clock-in', lat: v.clock_in_lat, lng: v.clock_in_lng, zip: v.clock_in_zip, own: v.clock_in_ownership },
        { kind: 'Clock-out', lat: v.clock_out_lat, lng: v.clock_out_lng, zip: v.clock_out_zip, own: v.clock_out_ownership },
      ];
      for (const e of ends) {
        if (e.lat == null || e.lng == null || (e.lat === 0 && e.lng === 0)) continue;
        const o = OWNERSHIP_STYLE[e.own?.status ?? 'unknown'];
        const ownerNote = e.own?.status === 'other' && e.own.owners.length ? ` (owned by ${esc(e.own.owners.join(', '))})` : '';
        const tooltip = `
          <div style="font:11px/1.4 system-ui;min-width:180px">
            <div style="font-weight:600;margin-bottom:2px">${e.kind} · ${start.toLocaleString()}</div>
            <div style="color:#555">${e.lat.toFixed(6)}, ${e.lng.toFixed(6)} (zip ${e.zip ?? '—'})</div>
            <div style="color:#555">Caregiver: ${esc((v.caregiver_name ?? v.caregiver_id ?? 'unknown').toString())}</div>
            <div style="color:#555">Duration: ${v.duration_minutes != null ? (v.duration_minutes / 60).toFixed(2) + 'h' : '—'}</div>
            <div style="color:${o.color};font-weight:500;margin-top:2px">${o.label}${ownerNote}</div>
          </div>`;
        L.circleMarker([e.lat, e.lng], e.kind === 'Clock-in'
          ? { radius: 6, color: o.color, fillColor: o.color, fillOpacity: 0.7, weight: 1.5 }
          : { radius: 6, color: o.color, fillColor: '#ffffff', fillOpacity: 0.9, weight: 2.5 },
        ).bindTooltip(tooltip, { sticky: true, opacity: 1 }).addTo(layerRef.current);
        points.push([e.lat, e.lng]);
      }
    }
    if (points.length > 0) {
      mapRef.current.fitBounds(L.latLngBounds(points), { padding: [24, 24], maxZoom: 15 });
    }
    mapRef.current.invalidateSize();
  }, [mapReady, visits, dominantZip]);

  useEffect(() => {
    const id = setTimeout(() => mapRef.current?.invalidateSize(), 150);
    return () => clearTimeout(id);
  }, [height]);

  return (
    <div style={{ position: 'relative', width: '100%', height }}>
      <div
        ref={containerRef}
        style={{ width: '100%', height: '100%', background: 'var(--surface-2)', borderRadius: 6, border: '1px solid var(--border)' }}
      />
      <div style={{
        position: 'absolute', left: 8, bottom: 8, padding: '4px 8px', background: 'var(--surface)',
        border: '1px solid var(--border)', borderRadius: 3, fontSize: 10.5, color: 'var(--text-muted)',
        pointerEvents: 'none', zIndex: 400, display: 'flex', gap: 10, flexWrap: 'wrap',
      }}>
        {(['own', 'other', 'unowned', 'unknown'] as const).map((k) => (
          <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 8, height: 8, borderRadius: 4, background: OWNERSHIP_STYLE[k].color }} />
            {OWNERSHIP_STYLE[k].label}
          </span>
        ))}
        <span>● clock-in · ○ clock-out</span>
      </div>
    </div>
  );
}

type ZipOwnership = { status: 'own' | 'other' | 'unowned' | 'unknown'; owners: string[] };
const OWNERSHIP_STYLE: Record<ZipOwnership['status'], { color: string; label: string }> = {
  own:     { color: '#10b981', label: 'In territory' },
  other:   { color: '#ef4444', label: 'Out of territory' },
  unowned: { color: '#9ca3af', label: 'Zip in no territory' },
  unknown: { color: '#9ca3af', label: 'Zip unknown' },
};

interface VisitDetail {
  visit_id: string;
  caregiver_id: string | null;
  caregiver_name: string | null;
  start_time: string;
  end_time: string | null;
  duration_minutes: number | null;
  actual_clock_in: string | null;
  actual_clock_out: string | null;
  actual_minutes: number | null;
  entry_type: 'mobile' | 'telephony' | 'manual';
  clock_in_lat: number | null;
  clock_in_lng: number | null;
  clock_in_zip: string | null;
  clock_out_lat: number | null;
  clock_out_lng: number | null;
  clock_out_zip: string | null;
  clock_in_ownership: ZipOwnership | null;
  clock_out_ownership: ZipOwnership | null;
  service_code: string | null;
  payer_name: string | null;
  billing_rate: number | null;
  billing_total_cents: number;
  last_synced_at: string | null;
}

function VisitsTab({ c }: { c: Case }) {
  const [visits, setVisits] = useState<VisitDetail[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<VisitDetail | null>(null);
  useEffect(() => {
    api<{ visits: VisitDetail[] }>(`/api/cases/${c.id.replace('CASE-', '').replace(/^0+/, '')}/visits`)
      .then((d) => setVisits(d.visits))
      .catch((e) => setError(e instanceof Error ? e.message : 'load failed'));
  }, [c.id]);

  if (error) return <div className="card card-pad muted" style={{ fontSize: 12.5 }}>Failed to load visits: {error}</div>;
  if (!visits) return <div className="card card-pad muted" style={{ fontSize: 12.5 }}>Loading visits…</div>;
  if (visits.length === 0) return <div className="card card-pad muted" style={{ fontSize: 12.5 }}>No visits in case window.</div>;

  return (
    <div className="card">
      <div className="sec-head"><h3>Visits in case window · {visits.length} total</h3></div>
      <table className="tbl">
        <thead><tr>
          <th>Date</th>
          <th>Caregiver</th>
          <th style={{ textAlign: 'right' }}>Hours</th>
          <th>Entry</th>
          <th>Clock-in zip</th>
          <th style={{ textAlign: 'right' }}>$</th>
        </tr></thead>
        <tbody>
          {visits.map((v) => {
            const date = new Date(v.start_time);
            const zipStyle = OWNERSHIP_STYLE[v.clock_in_ownership?.status ?? 'unknown'];
            return (
              <tr key={v.visit_id} className="clickable" onClick={() => setSelected(v)}>
                <td className="num">{date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })}</td>
                <td>{v.caregiver_name ?? <span className="faint mono" style={{ fontSize: 11 }}>CG-{v.caregiver_id ?? '?'}</span>}</td>
                <td className="num mono" style={{ textAlign: 'right' }}>{v.duration_minutes != null ? (v.duration_minutes / 60).toFixed(1) : '—'}</td>
                <td>
                  {v.entry_type === 'manual'
                    ? <span className="c-gold" style={{ fontSize: 12 }}>manual</span>
                    : v.entry_type === 'telephony'
                      ? <span className="muted" style={{ fontSize: 12 }}>phone EVV</span>
                      : <span className="muted" style={{ fontSize: 12 }}>GPS</span>}
                </td>
                <td className="mono" style={{ fontSize: 12, color: v.clock_in_zip ? zipStyle.color : undefined }} title={v.clock_in_zip ? zipStyle.label : undefined}>{v.clock_in_zip ?? '—'}</td>
                <td className="num mono" style={{ textAlign: 'right' }}>{formatCents(v.billing_total_cents)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {selected && <VisitDetailModal v={selected} c={c} onClose={() => setSelected(null)} />}
    </div>
  );
}

interface CaregiverMaster {
  id: number;
  axiscare_caregiver_id: string;
  name: string | null;
  email: string | null;
  phone_mobile: string | null;
  hire_date: string | null;
  termination_date: string | null;
  status: string | null;
  region_label: string | null;
  payroll_id: string | null;
  payrate_hourly: number | null;
  payrate_livein: number | null;
  caregiver_class: string | null;
}

function ZipOwnershipLabel({ zip, own }: { zip: string; own: ZipOwnership | null }) {
  const o = OWNERSHIP_STYLE[own?.status ?? 'unknown'];
  return (
    <span style={{ color: o.color }}>
      <span className="mono">{zip}</span> · {o.label}
      {own?.status === 'other' && own.owners.length ? ` (owned by ${own.owners.join(', ')})` : ''}
    </span>
  );
}

function VisitDetailModal({ v, c, onClose }: { v: VisitDetail; c: Case; onClose: () => void }) {
  const start = new Date(v.start_time);
  const end = v.end_time ? new Date(v.end_time) : null;
  const actualIn = v.actual_clock_in ? new Date(v.actual_clock_in) : null;
  const actualOut = v.actual_clock_out ? new Date(v.actual_clock_out) : null;
  const [cg, setCg] = useState<CaregiverMaster | null>(null);
  useEffect(() => {
    const num = c.id.replace(/^CASE-/, '').replace(/^0+/, '');
    api<{ caregiver: CaregiverMaster | null }>(`/api/cases/${num}/visits/${v.visit_id}/caregiver`)
      .then((d) => setCg(d.caregiver))
      .catch(() => setCg(null));
  }, [c.id, v.visit_id]);
  const fmtDateTime = (d: Date) => d.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
  const fmtTime = (d: Date) => d.toLocaleString('en-US', { timeStyle: 'short' });
  // Variance between scheduled and actual durations. Positive = caregiver
  // worked longer than scheduled; negative = clocked out early.
  const variance = (v.actual_minutes != null && v.duration_minutes != null)
    ? v.actual_minutes - v.duration_minutes
    : null;
  const inUrl = v.clock_in_lat != null && v.clock_in_lng != null
    ? `https://www.google.com/maps?q=${v.clock_in_lat},${v.clock_in_lng}` : null;
  const outUrl = v.clock_out_lat != null && v.clock_out_lng != null
    ? `https://www.google.com/maps?q=${v.clock_out_lat},${v.clock_out_lng}` : null;
  const driftMeters = (v.clock_in_lat != null && v.clock_in_lng != null && v.clock_out_lat != null && v.clock_out_lng != null)
    ? haversineMeters(v.clock_in_lat, v.clock_in_lng, v.clock_out_lat, v.clock_out_lng)
    : null;

  const Row = ({ k, val, mono }: { k: string; val: React.ReactNode; mono?: boolean }) => (
    <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 12, padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 12.5 }}>
      <div className="muted">{k}</div>
      <div className={mono ? 'mono' : ''}>{val ?? <span className="faint">—</span>}</div>
    </div>
  );

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: 'var(--surface)', borderRadius: 8, padding: 18, width: 640, maxHeight: '85vh', overflow: 'auto',
        border: '1px solid var(--border)', position: 'relative', zIndex: 1001,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
          <div>
            <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>
              Visit · {v.entry_type === 'manual' ? <span className="c-gold">manual entry</span> : v.entry_type === 'telephony' ? 'phone EVV' : 'GPS'}
            </div>
            <h3 style={{ margin: '4px 0 0', fontSize: 16, fontWeight: 600 }}>{fmtDateTime(start)}</h3>
            <div className="muted mono" style={{ fontSize: 11.5, marginTop: 2 }}>visit_id {v.visit_id}</div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><Icon name="close" size={12} /></button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            <div className="muted" style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Schedule</div>
            <Row k="Scheduled start" val={fmtDateTime(start)} />
            <Row k="Scheduled end" val={end ? fmtDateTime(end) : null} />
            <Row k="Scheduled duration" val={v.duration_minutes != null ? `${(v.duration_minutes / 60).toFixed(2)} h (${v.duration_minutes} min)` : null} />
            <Row k="Actual clock-in" val={actualIn ? fmtTime(actualIn) : null} />
            <Row k="Actual clock-out" val={actualOut ? fmtTime(actualOut) : null} />
            <Row k="Actual duration" val={v.actual_minutes != null ? `${(v.actual_minutes / 60).toFixed(2)} h (${v.actual_minutes} min)` : null} />
            {variance != null && variance !== 0 && (
              <Row
                k="Variance"
                val={
                  <span className={Math.abs(variance) >= 30 ? 'c-warn' : ''}>
                    {variance > 0 ? '+' : ''}{variance} min ({variance > 0 ? 'overran' : 'short'})
                  </span>
                }
              />
            )}
            <Row k="Service code" val={v.service_code} mono />
          </div>
          <div>
            <div className="muted" style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Caregiver</div>
            <Row k="Name" val={v.caregiver_name} />
            <Row k="ID" val={v.caregiver_id} mono />
            {cg && (
              <>
                <Row k="Status" val={cg.status} />
                <Row k="Hired" val={cg.hire_date ? parseDateOnly(cg.hire_date)!.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null} />
                {cg.termination_date && <Row k="Terminated" val={parseDateOnly(cg.termination_date)!.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} />}
                <Row k="Pay rate" val={cg.payrate_hourly != null ? `$${cg.payrate_hourly.toFixed(2)}/hr${cg.payrate_livein ? ` · $${cg.payrate_livein.toFixed(2)}/day` : ''}` : null} mono />
                <Row k="Region" val={cg.region_label} />
                <Row k="Class" val={cg.caregiver_class} />
                <Row k="Email" val={cg.email} />
                <Row k="Phone" val={cg.phone_mobile} />
              </>
            )}
          </div>
        </div>

        <div style={{ marginTop: 14 }}>
          <div className="muted" style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>GPS</div>
          <Row
            k="Clock-in"
            val={inUrl
              ? <a href={inUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }}>{v.clock_in_lat?.toFixed(6)}, {v.clock_in_lng?.toFixed(6)} ↗</a>
              : null}
            mono
          />
          <Row
            k="Clock-out"
            val={outUrl
              ? <a href={outUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }}>{v.clock_out_lat?.toFixed(6)}, {v.clock_out_lng?.toFixed(6)} ↗</a>
              : null}
            mono
          />
          <Row k="Clock-in zip" val={v.clock_in_zip ? <ZipOwnershipLabel zip={v.clock_in_zip} own={v.clock_in_ownership} /> : null} />
          <Row k="Clock-out zip" val={v.clock_out_zip ? <ZipOwnershipLabel zip={v.clock_out_zip} own={v.clock_out_ownership} /> : null} />
          <Row k="Drift (clock-in → clock-out)" val={driftMeters != null ? `${driftMeters.toFixed(0)} m` : null} />
        </div>

        <div style={{ marginTop: 14 }}>
          <div className="muted" style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Billing</div>
          <Row k="Payer" val={v.payer_name} />
          <Row k="Rate" val={v.billing_rate != null ? `$${v.billing_rate.toFixed(2)}/hr` : null} mono />
          <Row k="Total" val={formatCents(v.billing_total_cents)} mono />
        </div>

        <div className="muted" style={{ fontSize: 11, marginTop: 14, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
          Last synced from AxisCare: {v.last_synced_at ? new Date(v.last_synced_at).toLocaleString() : '—'}
        </div>
      </div>
    </div>
  );
}

function haversineMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

function PayersTab({ c }: { c: Case }) {
  const rows = c.payerBreakdown ?? [];
  if (rows.length === 0) {
    return (
      <div className="card card-pad muted" style={{ fontSize: 12.5 }}>
        No payer data on this case (visits not yet ingested with payer_name, or all payers blank in AxisCare).
      </div>
    );
  }
  // Same colour per payer as the payer split bar above.
  const colorOf = (name: string): string => payerColor(c.payerSplit, name);
  const grand = rows.reduce((s, r) => s + r.revenueCents, 0);
  return (
    <div className="card card-pad">
      <h3 style={{ margin: '0 0 12px', fontSize: 13, fontWeight: 600 }}>Payer × zip breakdown · case window</h3>
      <PayerBar split={c.payerSplit} />
      <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
        {rows.map((r) => {
          const pct = grand > 0 ? Math.round((r.revenueCents / grand) * 100) : 0;
          return (
            <div key={`${r.payer}|${r.zip}`} style={{ padding: 12, background: 'var(--surface-2)', borderRadius: 6, border: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                <span style={{ width: 10, height: 10, borderRadius: 2, background: colorOf(r.payer) }} />
                <span style={{ fontWeight: 500, fontSize: 13 }}>{r.payer}</span>
                <span className="muted" style={{ fontSize: 11.5, marginLeft: 'auto' }}>zip <span className="mono">{r.zip}</span></span>
              </div>
              <div className="num mono" style={{ fontSize: 22, fontWeight: 600 }}>{formatCents(r.revenueCents)}</div>
              <div className="muted" style={{ fontSize: 12 }}>{pct}% · {r.visits} visits · {(r.minutes / 60).toFixed(1)}h</div>
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: 12, padding: 10, background: 'var(--info-soft)', border: '1px solid color-mix(in oklch, var(--info) 30%, var(--border))', borderRadius: 6, fontSize: 12, color: 'var(--text)' }}>
        <strong>SPC matching:</strong> if an active Service Permission Contract covers any payer above
        (e.g., "AF-A grants AF-B all VA visits"), those visits are cleared. Other payers on this client may still constitute encroachment.
      </div>
    </div>
  );
}

interface CaregiverDetail {
  caregiver_id: string | null;
  caregiver_name: string | null;
  visits_count: number;
  hours: number;
  billing_total_cents: number;
  manual_rate: number;
}

function CaregiversTab({ c }: { c: Case }) {
  const [rows, setRows] = useState<CaregiverDetail[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api<{ caregivers: CaregiverDetail[] }>(`/api/cases/${c.id.replace('CASE-', '').replace(/^0+/, '')}/caregivers`)
      .then((d) => setRows(d.caregivers))
      .catch((e) => setError(e instanceof Error ? e.message : 'load failed'));
  }, [c.id]);

  if (error) return <div className="card card-pad muted" style={{ fontSize: 12.5 }}>Failed to load caregivers: {error}</div>;
  if (!rows) return <div className="card card-pad muted" style={{ fontSize: 12.5 }}>Loading caregivers…</div>;
  if (rows.length === 0) return <div className="card card-pad muted" style={{ fontSize: 12.5 }}>No caregivers in case window.</div>;

  return (
    <div className="card">
      <div className="sec-head"><h3>Caregivers on case · {rows.length}</h3></div>
      <table className="tbl">
        <thead><tr>
          <th>Caregiver</th>
          <th>ID</th>
          <th style={{ textAlign: 'right' }}>Visits</th>
          <th style={{ textAlign: 'right' }}>Hours</th>
          <th style={{ textAlign: 'right' }}>Manual</th>
          <th style={{ textAlign: 'right' }}>$</th>
        </tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.caregiver_id ?? `null-${r.visits_count}`}>
              <td>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div className="avatar" style={{ width: 24, height: 24, fontSize: 10 }}>
                    {r.caregiver_name
                      ? r.caregiver_name.split(/\s|,/).map(p => p[0]).filter(Boolean).slice(0, 2).join('')
                      : '?'}
                  </div>
                  {r.caregiver_name ?? <span className="faint">(unnamed)</span>}
                </div>
              </td>
              <td className="mono faint" style={{ fontSize: 12 }}>CG-{r.caregiver_id ?? '?'}</td>
              <td className="num mono" style={{ textAlign: 'right' }}>{r.visits_count}</td>
              <td className="num mono" style={{ textAlign: 'right' }}>{r.hours.toFixed(1)}</td>
              <td className="num mono" style={{ textAlign: 'right' }}>{(r.manual_rate * 100).toFixed(0)}%</td>
              <td className="num mono" style={{ textAlign: 'right' }}>{formatCents(r.billing_total_cents)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Decision 0006: the time between each step once a case is notified —
 * Notified → Acknowledged → Confirmed violation → Resolved — from the first
 * move into each state. Hidden until the case has been notified.
 */
function NoticeTimeline({ history }: { history: HistoryEntry[] }) {
  const first = (s: string) => history.find((h) => h.to_state === s)?.created_at ?? null;
  const notified = first('NOTIFIED');
  if (!notified) return null;
  const steps: { label: string; at: string | null }[] = [
    { label: 'Notified', at: notified },
    { label: 'Acknowledged', at: first('ACKD') },
    { label: 'Confirmed violation', at: first('CONFIRMED_VIOLATION') },
    { label: 'Resolved', at: [...history].reverse().find((h) => h.to_state === 'RESOLVED')?.created_at ?? null },
  ];
  const day = (d: string) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  let prev: string | null = null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 11.5, marginTop: 6 }}>
      {steps.map((s, i) => {
        const gap = s.at && prev ? Math.round((new Date(s.at).getTime() - new Date(prev).getTime()) / 86_400_000) : null;
        if (s.at) prev = s.at;
        return (
          <span key={s.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, opacity: s.at ? 1 : 0.45 }}>
            {i > 0 && <span className="faint">→{gap != null ? ` ${gap}d →` : ''}</span>}
            <span style={{ fontWeight: s.at ? 600 : 400 }}>{s.label}</span>
            {s.at && <span className="muted">{day(s.at)}</span>}
          </span>
        );
      })}
    </div>
  );
}

interface HistoryEntry {
  id: number;
  from_state: string | null;
  to_state: string;
  reason: string | null;
  created_at: string;
  actor_user_id: number | null;
  actor_name: string | null;
  actor_email: string | null;
}

function HistoryTab({ c }: { c: Case }) {
  const [history, setHistory] = useState<HistoryEntry[] | null>(null);
  useEffect(() => {
    const num = c.id.replace(/^CASE-/, '').replace(/^0+/, '');
    api<{ history: HistoryEntry[] }>(`/api/cases/${num}`)
      .then((d) => setHistory(d.history))
      .catch(() => setHistory([]));
  }, [c.id]);

  // Always-on lead events derived from the case itself: the audit run that opened it.
  const seed: { who: string; what: string; meta?: string; when: string; isSystem: boolean }[] = [
    { who: 'system', what: `Case opened in NEW · ${c.flags.length} flag${c.flags.length === 1 ? '' : 's'} raised`, meta: c.audit.runId ? `Run ${c.audit.runId}` : undefined, when: timeAgo(c.openedAt), isSystem: true },
  ];
  const transitions = (history ?? []).map((h) => ({
    who: h.actor_name ?? h.actor_email ?? `user #${h.actor_user_id ?? '?'}`,
    what: h.from_state ? `${h.from_state} → ${h.to_state}` : `Set state to ${h.to_state}`,
    meta: h.reason ?? undefined,
    when: timeAgo(new Date(h.created_at)),
    isSystem: false,
  }));
  const events = [...seed, ...transitions];

  return (
    <div className="card" style={{ padding: 0 }}>
      {!history && <div className="muted card-pad" style={{ fontSize: 12.5 }}>Loading history…</div>}
      {history && (
        <div style={{ padding: 16 }}>
          {events.map((e, i) => (
            <div key={i} style={{ display: 'flex', gap: 12, paddingBottom: 14, position: 'relative' }}>
              <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div className="avatar" style={{ width: 24, height: 24, fontSize: 10, background: e.isSystem ? 'var(--surface-3)' : 'var(--accent-soft)', color: e.isSystem ? 'var(--text-muted)' : 'var(--accent)' }}>
                  {e.isSystem ? <Icon name="server" size={11} /> : e.who.split(/[\s@]/).map(p => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase()}
                </div>
                {i < events.length - 1 && <div style={{ flex: 1, width: 1, background: 'var(--border)', marginTop: 2 }} />}
              </div>
              <div style={{ flex: 1, paddingBottom: 4 }}>
                <div style={{ fontSize: 13 }}><span style={{ fontWeight: 500 }}>{e.who}</span> <span className="muted">{e.what}</span></div>
                {e.meta && <div className="muted" style={{ fontSize: 12 }}>{e.meta}</div>}
                <div className="faint" style={{ fontSize: 11, marginTop: 2 }}>{e.when}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function timeAgo(d: Date | string): string {
  const dt = d instanceof Date ? d : new Date(d);
  const m = Math.floor((Date.now() - dt.getTime()) / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function RawTab({ c }: { c: Case }) {
  const json = JSON.stringify({
    case_id: c.id,
    patient: { axiscare_id: c.axiscareClientId, name: c.patient, location: c.location },
    franchisee: { af: c.franchiseeAf, name: c.franchiseeName },
    territory_owner: c.ownerAf ? { af: c.ownerAf, name: c.ownerName } : 'self',
    dominant_zip: c.dominantZip,
    z_eval_source: c.zipOnlyTag ? 'patient_record_zip' : 'dominant_clock_in',
    audit_run: { id: c.audit.runId, runtime_ms: c.audit.runtimeMs, spec_version: c.audit.specVersion },
    flags: c.flags.map(f => ({ rule: FLAG_TYPES[f]?.ruleId ?? f, severity: FLAG_TYPES[f]?.sev ?? 'info', details: c.flagDetails?.[f] ?? null })),
    metrics: {
      hours: c.hours,
      total_revenue_cents: c.totalRevenueCents,
      visits: c.visits,
      caregivers: c.caregivers,
      manual_rate: c.manualRate,
    },
    payer_split: c.payerSplit,
    grandfather: {
      client_start_of_care: c.startOfCare ? ymdLocal(c.startOfCare) : null,
      owner_effective_date: c.ownerEffectiveDate ? ymdLocal(c.ownerEffectiveDate) : null,
      owner_effective_date_source: c.ownerEffectiveDateSource,
      days_grandfather: c.daysGrandfather,
    },
    state: c.state,
  }, null, 2);
  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      <div className="sec-head"><h3>Raw audit JSON</h3></div>
      <pre style={{ margin: 0, padding: 16, fontSize: 12, fontFamily: 'var(--font-mono)', background: 'var(--surface-2)', overflow: 'auto', lineHeight: 1.55, color: 'var(--text)' }}>{json}</pre>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
      <span className="muted">{label}</span>
      <span className="num mono" style={{ fontWeight: 500 }}>{value}</span>
    </div>
  );
}


// ---- Financial tab: the owed-amount calculator (David 8/31) ----
// Semi-manual by design: measured window dollars from AxisCare (billable =
// what the offending franchisee invoiced; payable = caregiver cost with every
// pay-rate rule already applied per visit) + reviewer-supplied assumptions
// for the remaining variable costs. Nothing here is persisted — it is a
// working calculator to support the human make-whole process.
interface FinTotals {
  visits: number; billable_cents: number; payable_cents: number;
  visits_with_billable: number; visits_with_payable: number; minutes: number;
}
interface FinData {
  window_start: string; window_end: string;
  totals: FinTotals;
  by_payer: (FinTotals & { payer: string })[];
  by_month: (FinTotals & { month: string })[];
}

function FinancialTab({ c }: { c: Case }) {
  const [data, setData] = useState<FinData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [variablePct, setVariablePct] = useState('0');
  const [flatCosts, setFlatCosts] = useState('0');
  useEffect(() => {
    api<FinData>(`/api/cases/${c.id.replace(/^CASE-/, '').replace(/^0+/, '')}/financials`)
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : 'load failed'));
  }, [c.id]);
  if (error) return <div className="c-danger" style={{ fontSize: 13 }}>{error}</div>;
  if (!data) return <div className="muted" style={{ fontSize: 13 }}>Loading financials…</div>;

  const t = data.totals;
  const pct = Math.max(0, parseFloat(variablePct) || 0);
  const flat = Math.max(0, parseFloat(flatCosts) || 0);
  const revenue = t.billable_cents / 100;
  const payable = t.payable_cents / 100;
  const otherCosts = revenue * (pct / 100) + flat;
  const owed = revenue - payable - otherCosts;
  const window = `${String(data.window_start).slice(0, 10)} → ${String(data.window_end).slice(0, 10)}`;

  const missingBillable = t.visits - t.visits_with_billable;
  const noPayable = t.visits_with_payable === 0;

  const Row = ({ label, value, strong, danger }: { label: string; value: string; strong?: boolean; danger?: boolean }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: strong ? 15 : 13, fontWeight: strong ? 700 : 400 }}>
      <span className={strong ? '' : 'muted'}>{label}</span>
      <span className={`mono ${danger ? 'c-danger' : ''}`}>{value}</span>
    </div>
  );
  const cell = (v: number) => formatCents(v);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 860 }}>
      {(missingBillable > 0 || noPayable) && (
        <div className="c-warn" style={{ border: '1px dashed var(--warn)', borderRadius: 8, padding: '10px 14px', fontSize: 12.5 }}>
          <strong>Incomplete billing data</strong> — {missingBillable > 0 && <>{missingBillable} of {t.visits} visits have no billable amount. </>}
          {noPayable && <>No caregiver-payable amounts ingested for this site yet (billing re-ingest pending). </>}
          Totals below understate reality until the next billing ingest for this site completes.
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <div className="card card-pad">
          <div style={{ fontSize: 11.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600, marginBottom: 8 }}>
            Measured · {window}
          </div>
          <Row label={`Revenue billed (${t.visits_with_billable}/${t.visits} visits)`} value={cell(t.billable_cents)} />
          <Row label={`Caregiver payable (${t.visits_with_payable}/${t.visits} visits)`} value={cell(t.payable_cents)} />
          <Row label="Hours" value={(t.minutes / 60).toFixed(1)} />
        </div>
        <div className="card card-pad">
          <div style={{ fontSize: 11.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600, marginBottom: 8 }}>
            Assumptions (yours)
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
            <div style={{ flex: 1 }}>
              <label className="muted" style={{ display: 'block', fontSize: 11.5, marginBottom: 3 }}>Other variable costs (% of revenue)</label>
              <input className="input" type="number" min="0" max="100" step="0.5" value={variablePct} onChange={(e) => setVariablePct(e.target.value)} />
            </div>
            <div style={{ flex: 1 }}>
              <label className="muted" style={{ display: 'block', fontSize: 11.5, marginBottom: 3 }}>Flat costs ($)</label>
              <input className="input" type="number" min="0" step="10" value={flatCosts} onChange={(e) => setFlatCosts(e.target.value)} />
            </div>
          </div>
          <div className="muted" style={{ fontSize: 11.5, marginTop: 8, lineHeight: 1.45 }}>
            Payable already includes every AxisCare pay-rate rule (per-client overrides, weekend/holiday bumps) per visit — only add costs NOT in the caregiver payable.
          </div>
        </div>
      </div>

      <div className="card card-pad">
        <Row label="Revenue billed while encroaching" value={cell(t.billable_cents)} />
        <Row label="− Caregiver payable" value={`(${cell(t.payable_cents)})`} />
        <Row label={`− Other variable costs (${pct}% + $${flat.toFixed(0)})`} value={`(${cell(Math.round(otherCosts * 100))})`} />
        <div className="divider-h" style={{ margin: '8px 0' }} />
        <Row label="Estimated net profit / amount owed" value={cell(Math.round(owed * 100))} strong danger={owed < 0} />
        <div className="muted" style={{ fontSize: 11.5, marginTop: 6 }}>
          Working estimate to support the make-whole process between franchisees — not an invoice. Final numbers are agreed by AFI.
        </div>
      </div>

      {[{ title: 'By payer', key: 'payer' as const, rows: data.by_payer.map((r) => ({ label: r.payer, ...r })) },
        { title: 'By month', key: 'month' as const, rows: data.by_month.map((r) => ({ label: r.month, ...r })) }].map((sec) => (
        <div key={sec.key} className="card">
          <div className="sec-head"><h3>{sec.title}</h3></div>
          <div style={{ overflowX: 'auto' }}>
            <table className="table" style={{ width: '100%', fontSize: 12.5 }}>
              <thead><tr><th>{sec.title === 'By payer' ? 'Payer' : 'Month'}</th><th style={{ textAlign: 'right' }}>Visits</th><th style={{ textAlign: 'right' }}>Hours</th><th style={{ textAlign: 'right' }}>Billable</th><th style={{ textAlign: 'right' }}>Payable</th><th style={{ textAlign: 'right' }}>Margin</th></tr></thead>
              <tbody>
                {sec.rows.map((r) => (
                  <tr key={r.label}>
                    <td>{r.label}</td>
                    <td className="num mono" style={{ textAlign: 'right' }}>{r.visits}</td>
                    <td className="num mono" style={{ textAlign: 'right' }}>{(r.minutes / 60).toFixed(1)}</td>
                    <td className="num mono" style={{ textAlign: 'right' }}>{cell(r.billable_cents)}</td>
                    <td className="num mono" style={{ textAlign: 'right' }}>{cell(r.payable_cents)}</td>
                    <td className="num mono" style={{ textAlign: 'right' }}>{cell(r.billable_cents - r.payable_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}
